import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Globe, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Upload,
  Download,
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Clock,
  Zap,
  Users,
  Eye,
  EyeOff,
  X,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import ContentStatusControl from './ContentStatusControl';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

const CATEGORIES = [
  'External Learning',
  'Compliance & Regulatory',
  'Leadership & Management',
  'IT & Technology',
  'Finance & Accounting',
  'Risk Management',
  'Ethics & Governance'
];

const PROVIDERS = [
  { value: 'scorm_cloud', label: 'SCORM Cloud' },
  { value: 'intuition', label: 'Intuition' },
  { value: 'custom', label: 'Custom Provider' },
];

const CONTENT_TYPES = [
  { value: 'aicc', label: 'AICC' },
  { value: 'scorm_12', label: 'SCORM 1.2' },
  { value: 'scorm_2004', label: 'SCORM 2004' },
  { value: 'xapi', label: 'xAPI' },
];

const DEFAULT_MODULE = {
  title: '',
  description: '',
  iframe_url: '',
  provider: 'scorm_cloud',
  content_type: 'aicc',
  category: 'External Learning',
  estimated_duration_minutes: 30,
  xp_reward: 100,
  thumbnail_url: '',
};

export default function ExternalLearningAdmin({ token }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [analytics, setAnalytics] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({ ...DEFAULT_MODULE });
  const [formLoading, setFormLoading] = useState(false);
  
  // Bulk import state
  const [bulkMode, setBulkMode] = useState('manual');
  const [bulkModules, setBulkModules] = useState([{ ...DEFAULT_MODULE }]);
  const [csvFile, setCsvFile] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);

  useEffect(() => {
    fetchModules();
    fetchAnalytics();
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/external-learning/modules?status=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModules(res.data || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast.error('Failed to load external learning modules');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/external-learning/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleCreateModule = async () => {
    if (!formData.title || !formData.iframe_url) {
      toast.error('Title and iframe URL are required');
      return;
    }
    
    setFormLoading(true);
    try {
      await axios.post(`${API}/external-learning/modules`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Module created successfully');
      setShowCreateModal(false);
      setFormData({ ...DEFAULT_MODULE });
      fetchModules();
      fetchAnalytics();
    } catch (error) {
      console.error('Error creating module:', error);
      toast.error(error.response?.data?.detail || 'Failed to create module');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateModule = async () => {
    if (!formData.title || !formData.iframe_url) {
      toast.error('Title and iframe URL are required');
      return;
    }
    
    setFormLoading(true);
    try {
      await axios.put(`${API}/external-learning/modules/${editingModule.module_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Module updated successfully');
      setEditingModule(null);
      setFormData({ ...DEFAULT_MODULE });
      fetchModules();
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    try {
      await axios.delete(`${API}/external-learning/modules/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Module deleted successfully');
      setDeleteConfirm(null);
      fetchModules();
      fetchAnalytics();
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete module');
    }
  };

  const handleToggleStatus = async (module) => {
    const newStatus = module.status === 'active' ? 'archived' : 'active';
    try {
      await axios.put(`${API}/external-learning/modules/${module.module_id}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Module ${newStatus === 'active' ? 'activated' : 'archived'}`);
      fetchModules();
    } catch (error) {
      toast.error('Failed to update module status');
    }
  };

  const handleBulkImport = async () => {
    if (bulkMode === 'manual') {
      const validModules = bulkModules.filter(m => m.title && m.iframe_url);
      if (validModules.length === 0) {
        toast.error('Please add at least one valid module');
        return;
      }
      
      setFormLoading(true);
      try {
        const res = await axios.post(`${API}/external-learning/modules/bulk-import`, {
          modules: validModules,
          default_category: 'External Learning',
          auto_activate: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBulkResults(res.data);
        if (res.data.created_count > 0) {
          toast.success(`Created ${res.data.created_count} modules`);
        }
        fetchModules();
        fetchAnalytics();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Bulk import failed');
      } finally {
        setFormLoading(false);
      }
    } else if (bulkMode === 'csv') {
      // Handle CSV/Excel file upload
      if (!csvFile) {
        toast.error('Please select a CSV or Excel file');
        return;
      }
      
      setFormLoading(true);
      try {
        const formData = new FormData();
        formData.append('file', csvFile);
        formData.append('auto_activate', 'true');
        
        const res = await axios.post(`${API}/external-learning/modules/bulk-import-file`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        setBulkResults(res.data);
        if (res.data.created_count > 0) {
          toast.success(`Created ${res.data.created_count} modules`);
        }
        fetchModules();
        fetchAnalytics();
        setCsvFile(null);
      } catch (error) {
        console.error('Bulk import error:', error);
        toast.error(error.response?.data?.detail || 'Bulk import failed');
      } finally {
        setFormLoading(false);
      }
    }
  };

  const downloadCsvTemplate = () => {
    const template = 'title,iframe_url,description,provider,content_type,category,duration_minutes,xp_reward\n"Course Title","https://cloud.scorm.com/dispatch/...",Course description","scorm_cloud","aicc","External Learning",30,100';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'external_learning_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openEditModal = (module) => {
    setFormData({
      title: module.title,
      description: module.description || '',
      iframe_url: module.iframe_url,
      provider: module.provider || 'scorm_cloud',
      content_type: module.content_type || 'aicc',
      category: module.category || 'External Learning',
      estimated_duration_minutes: module.estimated_duration_minutes || 30,
      xp_reward: module.xp_reward || 100,
      thumbnail_url: module.thumbnail_url || '',
    });
    setEditingModule(module);
  };

  const filteredModules = modules.filter(m => 
    !search || 
    m.title.toLowerCase().includes(search.toLowerCase()) ||
    m.provider?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="external-learning-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
            <Globe className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">External Courses</h2>
            <p className="text-sm text-zinc-400">Manage SCORM Cloud, AICC & third-party iframe content</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowBulkModal(true)}
            data-testid="bulk-import-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Bulk Import
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
            data-testid="create-module-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Module
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Globe className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{analytics.total_modules}</p>
                <p className="text-xs text-zinc-500">Total Modules</p>
              </div>
            </div>
          </div>
          
          <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{analytics.total_completions}</p>
                <p className="text-xs text-zinc-500">Completions</p>
              </div>
            </div>
          </div>
          
          <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{analytics.unique_learners}</p>
                <p className="text-xs text-zinc-500">Unique Learners</p>
              </div>
            </div>
          </div>
          
          <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{analytics.in_progress_count}</p>
                <p className="text-xs text-zinc-500">In Progress</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search modules..."
          className="pl-12 bg-navigate-surface border-zinc-800"
          data-testid="module-search"
        />
      </div>

      {/* Modules Table */}
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Module</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Provider</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Duration</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">XP</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Completions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center">
                  <Loader2 className="h-8 w-8 text-navigate-gold animate-spin mx-auto" />
                </td>
              </tr>
            ) : filteredModules.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">
                  No external learning modules found
                </td>
              </tr>
            ) : (
              filteredModules.map((module) => (
                <tr key={module.module_id} className="hover:bg-zinc-800/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                        <Globe className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{module.title}</p>
                        <p className="text-xs text-zinc-500 truncate max-w-[200px]">{module.iframe_url}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full capitalize">
                      {module.provider?.replace('_', ' ') || 'SCORM Cloud'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-sm">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {module.estimated_duration_minutes || 30} mins
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-navigate-gold">
                      <Zap className="h-4 w-4" />
                      <span className="text-sm font-medium">{module.xp_reward || 100}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-sm">
                    {module.completion_count || 0}
                  </td>
                  <td className="px-4 py-3">
                    {/* Unified Draft / Published (active) / Archived status */}
                    <ContentStatusControl token={token} contentType="external_module" itemId={module.module_id} item={module} publishedValue="active" onChange={() => fetchModules()} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <a
                        href={module.iframe_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded"
                        title="Preview"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => openEditModal(module)}
                        className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(module)}
                        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || !!editingModule} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setEditingModule(null);
          setFormData({ ...DEFAULT_MODULE });
        }
      }}>
        <DialogContent className="bg-navigate-surface border-zinc-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingModule ? 'Edit Module' : 'Add External Learning Module'}
            </DialogTitle>
            <DialogDescription>
              {editingModule ? 'Update the module details below' : 'Create a new SCORM Cloud or third-party content module'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Course title"
                  className="bg-navigate-dark border-zinc-700 mt-1"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Iframe URL *</Label>
                <Input
                  value={formData.iframe_url}
                  onChange={(e) => setFormData({ ...formData, iframe_url: e.target.value })}
                  placeholder="https://cloud.scorm.com/dispatch/..."
                  className="bg-navigate-dark border-zinc-700 mt-1"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the module"
                  className="bg-navigate-dark border-zinc-700 mt-1"
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Provider</Label>
                <Select 
                  value={formData.provider} 
                  onValueChange={(v) => setFormData({ ...formData, provider: v })}
                >
                  <SelectTrigger className="bg-navigate-dark border-zinc-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navigate-surface border-zinc-700">
                    {PROVIDERS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Content Type</Label>
                <Select 
                  value={formData.content_type} 
                  onValueChange={(v) => setFormData({ ...formData, content_type: v })}
                >
                  <SelectTrigger className="bg-navigate-dark border-zinc-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navigate-surface border-zinc-700">
                    {CONTENT_TYPES.map(ct => (
                      <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger className="bg-navigate-dark border-zinc-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navigate-surface border-zinc-700">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={formData.estimated_duration_minutes}
                  onChange={(e) => setFormData({ ...formData, estimated_duration_minutes: parseInt(e.target.value) || 30 })}
                  className="bg-navigate-dark border-zinc-700 mt-1"
                />
              </div>
              
              <div>
                <Label>XP Reward</Label>
                <Input
                  type="number"
                  value={formData.xp_reward}
                  onChange={(e) => setFormData({ ...formData, xp_reward: parseInt(e.target.value) || 100 })}
                  className="bg-navigate-dark border-zinc-700 mt-1"
                />
              </div>
              
              <div>
                <Label>Thumbnail URL (optional)</Label>
                <Input
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-navigate-dark border-zinc-700 mt-1"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              onClick={editingModule ? handleUpdateModule : handleCreateModule}
              disabled={formLoading || !formData.title || !formData.iframe_url}
              className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
            >
              {formLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : editingModule ? (
                'Update Module'
              ) : (
                'Create Module'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="bg-navigate-surface border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Module?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.title}&quot;? This will also delete all progress records. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              onClick={() => handleDeleteModule(deleteConfirm?.module_id)}
              className="bg-red-600 hover:bg-red-500"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={showBulkModal} onOpenChange={(open) => {
        if (!open) {
          setShowBulkModal(false);
          setBulkResults(null);
          setBulkModules([{ ...DEFAULT_MODULE }]);
        }
      }}>
        <DialogContent className="bg-navigate-surface border-zinc-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Bulk Import External Learning Modules</DialogTitle>
            <DialogDescription>
              Import multiple SCORM Cloud or third-party modules at once
            </DialogDescription>
          </DialogHeader>
          
          {!bulkResults ? (
            <>
              <Tabs value={bulkMode} onValueChange={setBulkMode}>
                <TabsList className="bg-navigate-dark">
                  <TabsTrigger value="manual">Manual Entry</TabsTrigger>
                  <TabsTrigger value="csv">CSV Upload</TabsTrigger>
                </TabsList>
                
                <TabsContent value="manual" className="space-y-4 mt-4">
                  {bulkModules.map((module, idx) => (
                    <div key={idx} className="p-4 bg-navigate-dark border border-zinc-700 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-zinc-300">Module {idx + 1}</span>
                        {bulkModules.length > 1 && (
                          <button
                            onClick={() => setBulkModules(bulkModules.filter((_, i) => i !== idx))}
                            className="p-1 text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          value={module.title}
                          onChange={(e) => {
                            const updated = [...bulkModules];
                            updated[idx].title = e.target.value;
                            setBulkModules(updated);
                          }}
                          placeholder="Title *"
                          className="bg-zinc-900 border-zinc-700"
                        />
                        <Input
                          value={module.iframe_url}
                          onChange={(e) => {
                            const updated = [...bulkModules];
                            updated[idx].iframe_url = e.target.value;
                            setBulkModules(updated);
                          }}
                          placeholder="Iframe URL *"
                          className="bg-zinc-900 border-zinc-700"
                        />
                      </div>
                    </div>
                  ))}
                  
                  <button
                    onClick={() => setBulkModules([...bulkModules, { ...DEFAULT_MODULE }])}
                    className="w-full p-3 border-2 border-dashed border-zinc-700 rounded-lg text-zinc-400 hover:text-white hover:border-zinc-500 flex items-center justify-center gap-2"
                  >
                    <Plus className="h-5 w-5" />
                    Add Another Module
                  </button>
                </TabsContent>
                
                <TabsContent value="csv" className="space-y-4 mt-4">
                  <div className="flex justify-between items-center p-4 bg-navigate-dark rounded-lg">
                    <div>
                      <p className="text-white text-sm">Download CSV Template</p>
                      <p className="text-xs text-zinc-500">Use this template to format your data</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={downloadCsvTemplate}>
                      <Download className="h-4 w-4 mr-2" />
                      Template
                    </Button>
                  </div>
                  
                  <div className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      {csvFile ? (
                        <div className="flex flex-col items-center gap-2">
                          <CheckCircle className="h-12 w-12 text-green-400" />
                          <p className="text-white">{csvFile.name}</p>
                          <button 
                            onClick={(e) => { e.preventDefault(); setCsvFile(null); }}
                            className="text-xs text-zinc-400 hover:text-red-400"
                          >
                            Clear selection
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <FileSpreadsheet className="h-12 w-12 text-zinc-500" />
                          <p className="text-white">Click to upload CSV or Excel file</p>
                          <p className="text-xs text-zinc-500">Supports .csv, .xlsx, .xls</p>
                        </div>
                      )}
                    </label>
                  </div>
                </TabsContent>
              </Tabs>
              
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  onClick={handleBulkImport}
                  disabled={formLoading || (bulkMode === 'manual' && !bulkModules.some(m => m.title && m.iframe_url)) || (bulkMode === 'csv' && !csvFile)}
                  className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
                >
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Import Modules
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                  <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{bulkResults.created_count}</p>
                  <p className="text-sm text-zinc-400">Created</p>
                </div>
                {bulkResults.error_count > 0 && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                    <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{bulkResults.error_count}</p>
                    <p className="text-sm text-zinc-400">Errors</p>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <DialogClose asChild>
                  <Button className="bg-navigate-gold hover:bg-navigate-gold/90 text-black">Done</Button>
                </DialogClose>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
